import {
  IAsyncOperationsSnapshot,
  IAsyncOperationState,
  ITopicTarget,
  TAsyncOperationStatus,
} from '../../../api/realtime/types';
import { getTopicKey } from '../../../api/realtime/helpers';
import { TState } from './types';

type TOperationsById = Record<string, IAsyncOperationState>;
/** The part of the state that follows the operations themselves */
type TTrackedOperations = Pick<
  TState,
  'operationsById' | 'awaitedOperations' | 'finishedOperationIds'
>;

const FINAL_STATUSES: TAsyncOperationStatus[] = ['Completed', 'Failed', 'Cancelled'];

export const isFinalStatus = (status?: string | null): boolean =>
  FINAL_STATUSES.includes(status as TAsyncOperationStatus);

/** The server writes up to 7 fractional digits, while Date.parse reads milliseconds only. */
export const toEpochMs = (utc?: string | null): number =>
  utc ? Date.parse(utc.replace(/(\.\d{3})\d+/, '$1')) : NaN;

const belongsTo = (operation: IAsyncOperationState, target: ITopicTarget): boolean =>
  operation.topic === target.topic && operation.serviceCenterId === target.serviceCenterId;

const omitKey = <T>(record: Record<string, T>, key: string): Record<string, T> => {
  if (!(key in record)) return record;
  const rest = { ...record };
  delete rest[key];
  return rest;
};

/**
 * A finished operation never changes again, and a finished state always wins: it can carry a
 * lower sequence when the server rebuilt a lost state. Otherwise the state with the highest
 * sequence wins, as updates can arrive twice or out of order.
 */
export const shouldApplyUpdate = (
  current: IAsyncOperationState | undefined,
  next: IAsyncOperationState
): boolean => {
  if (!current) return true;
  if (isFinalStatus(current.status)) return false;
  return isFinalStatus(next.status) || next.sequence > current.sequence;
};

/** Hands an operation that ended while its page watched to the page, which reloads its data. */
const markFinished = <T extends TTrackedOperations>(tracked: T, operationId: string): T => ({
  ...tracked,
  awaitedOperations: omitKey(tracked.awaitedOperations, operationId),
  finishedOperationIds: tracked.finishedOperationIds.includes(operationId)
    ? tracked.finishedOperationIds
    : [...tracked.finishedOperationIds, operationId],
});

/**
 * Applies one state of an operation. Updates are full states with the empty fields left out, so
 * an applied one replaces the stored state whole: merging would keep the counts of a previous step.
 * A final state ended while the page watched when the hub pushed it, when the page had the
 * operation running, or when this tab submitted it. It is decided here, state by state, because a
 * fast operation's first state can be its last, and React renders updates that come together once.
 */
const applyState = <T extends TTrackedOperations>(
  tracked: T,
  next: IAsyncOperationState,
  isPushed: boolean
): T => {
  const current = tracked.operationsById[next.operationId];
  if (!shouldApplyUpdate(current, next)) return tracked;
  const applied = {
    ...tracked,
    operationsById: { ...tracked.operationsById, [next.operationId]: next },
  };
  if (!isFinalStatus(next.status)) return applied;
  const isWatched = isPushed || !!current || next.operationId in tracked.awaitedOperations;
  return isWatched ? markFinished(applied, next.operationId) : applied;
};

/** Applies a state the hub pushed to a page that follows the operation, or a submit reported. */
export const applyOperationUpdate = <T extends TTrackedOperations>(
  tracked: T,
  next: IAsyncOperationState
): T => applyState(tracked, next, true);

/**
 * Applies a snapshot, which lists every running operation of its page. An operation of the page
 * it lacks is dropped, unless it was created after the snapshot: it finished long ago, went stale
 * in the queue or ended while the page was offline, and would keep the page's actions disabled.
 * For the same reason a dropped operation is no longer awaited.
 * An operation it shows finished at first sight counts only when this tab submitted it: any other
 * may have ended before the page opened, and the page loaded its data after that.
 */
export const applySnapshot = <T extends TTrackedOperations>(
  tracked: T,
  snapshot: IAsyncOperationsSnapshot
): T => {
  const merged = snapshot.operations.reduce(
    (result, operation) => applyState(result, operation, false),
    tracked
  );
  const listed = new Set(snapshot.operations.map(operation => operation.operationId));
  const generatedAt = toEpochMs(snapshot.generatedAtUtc);
  const staleIds = Object.values(merged.operationsById)
    .filter(
      operation =>
        belongsTo(operation, snapshot) &&
        !listed.has(operation.operationId) &&
        !(toEpochMs(operation.createdAtUtc ?? operation.updatedAtUtc) > generatedAt)
    )
    .map(operation => operation.operationId);
  if (!staleIds.length) return merged;
  const operationsById = { ...merged.operationsById };
  staleIds.forEach(operationId => delete operationsById[operationId]);
  return {
    ...merged,
    operationsById,
    awaitedOperations: staleIds.reduce(omitKey, merged.awaitedOperations),
    finishedOperationIds: merged.finishedOperationIds.filter(id => !staleIds.includes(id)),
  };
};

/**
 * This tab submitted the operation and waits for its end, even when no running state ever shows:
 * a fast operation can end before the page sees it run, or while the hub is down.
 */
export const awaitOperation = <T extends TTrackedOperations>(
  tracked: T,
  operationId: string,
  target: ITopicTarget
): T => {
  const operation = tracked.operationsById[operationId];
  // it ended before the submit answered: hand it to the page again, after the submit's changes
  if (operation && isFinalStatus(operation.status)) return markFinished(tracked, operationId);
  return {
    ...tracked,
    awaitedOperations: { ...tracked.awaitedOperations, [operationId]: getTopicKey(target) },
  };
};

/** A page starts following a topic: forget what an earlier visit of the page left behind. */
export const forgetTopic = <T extends TTrackedOperations>(tracked: T, target: ITopicTarget): T => {
  const topicKey = getTopicKey(target);
  const operationsById: TOperationsById = {};
  Object.values(tracked.operationsById).forEach(operation => {
    if (!belongsTo(operation, target)) operationsById[operation.operationId] = operation;
  });
  const awaitedOperations: Record<string, string> = {};
  Object.entries(tracked.awaitedOperations).forEach(([operationId, key]) => {
    if (key !== topicKey) awaitedOperations[operationId] = key;
  });
  return {
    ...tracked,
    operationsById,
    awaitedOperations,
    finishedOperationIds: tracked.finishedOperationIds.filter(id => id in operationsById),
  };
};

export const getTopicOperations = (
  operationsById: TOperationsById,
  target: ITopicTarget
): IAsyncOperationState[] =>
  Object.values(operationsById).filter(operation => belongsTo(operation, target));

/** The operations of a page that ended while it watched, in the order they ended */
export const getFinishedTopicOperations = (
  { operationsById, finishedOperationIds }: Omit<TTrackedOperations, 'awaitedOperations'>,
  target: ITopicTarget
): IAsyncOperationState[] =>
  finishedOperationIds
    .map(operationId => operationsById[operationId])
    .filter(operation => !!operation && belongsTo(operation, target));

export const isAwaitingTopic = (
  awaitedOperations: TState['awaitedOperations'],
  target: ITopicTarget
): boolean => Object.values(awaitedOperations).includes(getTopicKey(target));

const createdAtOrLast = (operation: IAsyncOperationState): number => {
  const createdAt = toEpochMs(operation.createdAtUtc);
  return Number.isNaN(createdAt) ? Number.MAX_SAFE_INTEGER : createdAt;
};

/** The unfinished operation created first: the one that holds the service center's lock. */
export const getActiveOperation = (
  operations: IAsyncOperationState[]
): IAsyncOperationState | null =>
  operations
    .filter(operation => !isFinalStatus(operation.status))
    .sort((a, b) => createdAtOrLast(a) - createdAtOrLast(b))[0] ?? null;

type TApiError = {
  response?: { data?: { error?: { message?: string; errors?: unknown } } };
};

/**
 * The operation a refused submit reports as still running: the 400 of a second sync carries it
 * in `error.errors`.
 */
export const getRunningOperationFromError = (err: unknown): IAsyncOperationState | null => {
  const errors = (err as TApiError)?.response?.data?.error?.errors;
  return errors && typeof errors === 'object' && 'operationId' in errors
    ? (errors as IAsyncOperationState)
    : null;
};

export const getApiErrorMessage = (err: unknown): string | undefined =>
  (err as TApiError)?.response?.data?.error?.message;
